import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Square, Activity, Wifi, Smartphone, Monitor, MessageCircle, Clock, Signal, Globe } from 'lucide-react';
import clsx from 'clsx';

type Platform = 'whatsapp' | 'signal';

interface TrackerData {
    rtt: number;
    avg: number;
    median: number;
    threshold: number;
    state: string;
    timestamp: number;
}

interface ActivityEvent {
    event: 'online' | 'standby' | 'offline';
    timestamp: number;
    rtt?: number;
}

interface NetworkEstimate {
    type: 'wifi' | 'mobile' | 'roaming' | 'unknown';
    label: string;
    confidence: number;
}

interface GeoEstimate {
    region: string;
    detail: string;
}

interface SessionStats {
    total: number;
    avgDurationMs: number | null;
    currentSessionMs: number | null;
}

interface DeviceInfo {
    jid: string;
    state: string;
    rtt: number;
    avg: number;
    network?: NetworkEstimate;
    geo?: GeoEstimate;
    activityLog?: ActivityEvent[];
    sessionStats?: SessionStats;
}

interface ContactCardProps {
    jid: string;
    displayNumber: string;
    data: TrackerData[];
    devices: DeviceInfo[];
    deviceCount: number;
    presence: string | null;
    profilePic: string | null;
    onRemove: () => void;
    privacyMode?: boolean;
    platform?: Platform;
}

function formatDuration(ms: number | null): string {
    if (!ms) return '-';
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}min ${s % 60}s`;
    return `${Math.floor(m / 60)}h ${m % 60}min`;
}

function formatTime(ts: number): string {
    return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const EVENT_COLORS = {
    online: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500', label: 'En ligne' },
    standby: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-400', label: 'Veille' },
    offline: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', label: 'Hors ligne' },
};

const NETWORK_ICONS = {
    wifi: { icon: '📶', color: 'text-blue-600' },
    mobile: { icon: '📱', color: 'text-orange-500' },
    roaming: { icon: '🌐', color: 'text-purple-500' },
    unknown: { icon: '❓', color: 'text-gray-400' },
};

export function ContactCard({
    jid, displayNumber, data, devices, deviceCount, presence,
    profilePic, onRemove, privacyMode = false, platform = 'whatsapp'
}: ContactCardProps) {
    const [showTimeline, setShowTimeline] = useState(false);

    const lastData = data[data.length - 1];
    const currentStatus = devices.length > 0
        ? (devices.find(d => d.state === 'OFFLINE')?.state ||
           devices.find(d => d.state.includes('Online'))?.state ||
           devices[0].state)
        : 'Unknown';

    const blurredNumber = privacyMode ? displayNumber.replace(/\d/g, '•') : displayNumber;

    // Prendre le premier device pour les infos réseau/geo
    const mainDevice = devices[0];
    const network = mainDevice?.network;
    const geo = mainDevice?.geo;
    const sessionStats = mainDevice?.sessionStats;
    const activityLog = mainDevice?.activityLog ?? [];
    const networkIcon = network ? NETWORK_ICONS[network.type] : NETWORK_ICONS.unknown;

    return (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className={clsx(
                        "px-2 py-1 rounded text-xs font-medium flex items-center gap-1",
                        platform === 'whatsapp' ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                    )}>
                        <MessageCircle size={12} />
                        {platform === 'whatsapp' ? 'WhatsApp' : 'Signal'}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">{blurredNumber}</h3>
                    {/* Network badge */}
                    {network && network.type !== 'unknown' && (
                        <span className={clsx("px-2 py-1 rounded text-xs font-medium flex items-center gap-1 bg-gray-100", networkIcon.color)}
                            title={`Confiance: ${Math.round(network.confidence * 100)}%`}>
                            {networkIcon.icon} {network.label}
                        </span>
                    )}
                    {/* Geo badge */}
                    {geo && (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-indigo-50 text-indigo-600 flex items-center gap-1"
                            title={geo.detail}>
                            <Globe size={11} /> {geo.region}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowTimeline(!showTimeline)}
                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors flex items-center gap-1"
                    >
                        <Clock size={14} /> Timeline
                    </button>
                    <button
                        onClick={onRemove}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 font-medium transition-colors text-sm"
                    >
                        <Square size={16} /> Stop
                    </button>
                </div>
            </div>

            <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Status Card */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center text-center">
                        <div className="relative mb-4">
                            <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-md">
                                {profilePic ? (
                                    <img src={profilePic} alt="Profile"
                                        className={clsx("w-full h-full object-cover", privacyMode && "blur-xl scale-110")} />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl">
                                        {platform === 'whatsapp' ? '💬' : '📡'}
                                    </div>
                                )}
                            </div>
                            <div className={clsx(
                                "absolute bottom-2 right-2 w-6 h-6 rounded-full border-2 border-white",
                                currentStatus === 'OFFLINE' ? "bg-red-500" :
                                currentStatus.includes('Online') ? "bg-green-500 animate-pulse" : "bg-yellow-400"
                            )} />
                        </div>

                        <h4 className="text-xl font-bold text-gray-900 mb-1">{blurredNumber}</h4>

                        <div className="flex items-center gap-2 mb-4">
                            <span className={clsx(
                                "px-3 py-1 rounded-full text-sm font-semibold",
                                currentStatus === 'OFFLINE' ? "bg-red-100 text-red-700" :
                                currentStatus.includes('Online') ? "bg-green-100 text-green-700" :
                                currentStatus === 'Standby' ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-700"
                            )}>{currentStatus}</span>
                        </div>

                        <div className="w-full space-y-2 border-t pt-4">
                            <div className="flex justify-between text-sm text-gray-600">
                                <span className="flex items-center gap-1"><Wifi size={14}/> Présence officielle</span>
                                <span className="font-medium">{presence || 'Inconnu'}</span>
                            </div>
                            <div className="flex justify-between text-sm text-gray-600">
                                <span className="flex items-center gap-1"><Smartphone size={14}/> Appareils</span>
                                <span className="font-medium">{deviceCount || 0}</span>
                            </div>
                            {sessionStats && (
                                <>
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span className="flex items-center gap-1"><Activity size={14}/> Sessions totales</span>
                                        <span className="font-medium">{sessionStats.total}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span className="flex items-center gap-1"><Clock size={14}/> Durée moy. session</span>
                                        <span className="font-medium">{formatDuration(sessionStats.avgDurationMs)}</span>
                                    </div>
                                    {sessionStats.currentSessionMs !== null && (
                                        <div className="flex justify-between text-sm text-green-600 font-medium">
                                            <span>⏱ Session en cours</span>
                                            <span>{formatDuration(sessionStats.currentSessionMs)}</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Geo detail */}
                        {geo && (
                            <div className="w-full mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100 text-left">
                                <p className="text-xs font-semibold text-indigo-600 mb-1">📍 Estimation géo (RTT)</p>
                                <p className="text-sm text-indigo-800">{geo.region}</p>
                                <p className="text-xs text-indigo-500">{geo.detail}</p>
                            </div>
                        )}
                    </div>

                    {/* Metrics & Chart */}
                    <div className="md:col-span-2 space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <div className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Activity size={13}/> RTT moyen</div>
                                <div className="text-2xl font-bold text-gray-900">{lastData?.avg.toFixed(0) || '-'} ms</div>
                            </div>
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <div className="text-xs text-gray-500 mb-1">Médiane globale</div>
                                <div className="text-2xl font-bold text-gray-900">{lastData?.median.toFixed(0) || '-'} ms</div>
                            </div>
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <div className="text-xs text-gray-500 mb-1">Seuil Online</div>
                                <div className="text-2xl font-bold text-blue-600">{lastData?.threshold.toFixed(0) || '-'} ms</div>
                            </div>
                        </div>

                        {/* RTT Chart */}
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 h-[220px]">
                            <h5 className="text-xs font-medium text-gray-500 mb-3">Historique RTT</h5>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis dataKey="timestamp" hide />
                                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} />
                                    <Tooltip
                                        labelFormatter={(t: any) => new Date(t as number).toLocaleTimeString('fr-FR')}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: 12 }}
                                    />
                                    <Line type="monotone" dataKey="avg" stroke="#3b82f6" strokeWidth={2} dot={false} name="RTT moy" isAnimationActive={false} />
                                    <Line type="step" dataKey="threshold" stroke="#ef4444" strokeDasharray="5 5" dot={false} name="Seuil" isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Timeline */}
                        {showTimeline && (
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <h5 className="text-xs font-semibold text-gray-500 uppercase mb-3">Timeline d'activité</h5>
                                {activityLog.length === 0 ? (
                                    <p className="text-sm text-gray-400">Aucune activité enregistrée</p>
                                ) : (
                                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                                        {[...activityLog].reverse().map((event, i) => {
                                            const c = EVENT_COLORS[event.event];
                                            return (
                                                <div key={i} className={clsx("flex items-center justify-between px-3 py-1.5 rounded-lg text-sm", c.bg)}>
                                                    <div className="flex items-center gap-2">
                                                        <div className={clsx("w-2 h-2 rounded-full", c.dot)} />
                                                        <span className={clsx("font-medium", c.text)}>{c.label}</span>
                                                        {event.rtt && <span className="text-xs text-gray-400">{event.rtt}ms</span>}
                                                    </div>
                                                    <span className="text-xs text-gray-500">{formatTime(event.timestamp)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
